/**
 * Heatmap calendrier — une cellule par jour, colorée par la valeur d'une métrique.
 *
 * Porté de Metrix (`widgets/CalendarHeatmapWidget.tsx`), re-branché sur le catalogue
 * de champs Orion (libellés FR + format d'affichage) et sur `chartStyle` (palette et
 * tooltip partagés — rien n'est redéfini ici).
 *
 * `buildCalendarHeatmapOption` est une fonction pure, testable sans DOM : c'est elle
 * qui porte toute la logique (projection jour → valeur, plage, échelle de couleur).
 */
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import type { WidgetConfig } from '../schemas';
import { resolveDimensionKey } from '../chartFormat';
import { useFieldCatalog, metricLabel, metricFormat, type FieldCatalog } from '../fieldCatalog';
import { formatMetricValue } from '../formatValue';
import { CHART_COLORS, TITLE_STYLE, TOOLTIP_STYLE } from '../chartStyle';

type Row = Record<string, string | number>;

interface WidgetData {
  current: Row[];
  previous: Row[] | null;
  meta: any;
}

interface WidgetProps {
  config: WidgetConfig;
  data: WidgetData | null;
  loading?: boolean;
  error?: string;
}

// Cadre + padding fournis par WidgetShell (WidgetRenderer) : ici, hauteur seulement.
const SHELL = 'h-full';

/** Le calendrier ECharts n'accepte que des dates ISO : GA4 renvoie `20260601`. */
function toIsoDate(value: string): string {
  if (/^\d{8}$/.test(value)) return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  return value;
}

/**
 * Rampe de couleur dérivée de la palette partagée : du fond neutre du builder
 * jusqu'à la couleur de série pleine (suffixes hex = opacité).
 */
function buildColorRamp(base: string): string[] {
  return ['#F4F5FA', `${base}33`, `${base}66`, `${base}B3`, base];
}

export function buildCalendarHeatmapOption(
  config: WidgetConfig,
  data: WidgetData,
  catalog: FieldCatalog | null,
) {
  const metrics = config.metrics ?? [];
  const metric = metrics[0];
  const format = metricFormat(catalog, metric);
  const label = metricLabel(catalog, metric, config.metric_labels);

  const dimKey = resolveDimensionKey(data.current, config.dimensions, metrics);

  const points = (data.current ?? [])
    .map((row) => ({
      date: toIsoDate(String(dimKey ? row[dimKey] ?? '' : '')),
      value: Number(row[metric]) || 0,
    }))
    .filter((p) => !!p.date)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const seriesData: [string, number][] = points.map((p) => [p.date, p.value]);

  // Plage du calendrier : les dates effectivement présentes. Sans données, on
  // affiche le mois courant plutôt que de laisser ECharts planter sur `range: []`.
  const range: [string, string] = points.length
    ? [points[0].date, points[points.length - 1].date]
    : [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD')];

  // 95e centile comme maximum : une journée exceptionnelle ne doit pas écraser
  // toute l'échelle de couleur.
  const sorted = points.map((p) => p.value).sort((a, b) => a - b);
  const max = sorted.length ? sorted[Math.floor(sorted.length * 0.95)] ?? sorted[sorted.length - 1] : 1;

  const base = (config.format?.color as string | undefined) ?? CHART_COLORS[0];
  const hasTitle = !!config.title;

  return {
    title: hasTitle ? { text: config.title, ...TITLE_STYLE } : undefined,
    tooltip: {
      ...TOOLTIP_STYLE,
      formatter: (p: { value: [string, number] }) => {
        const day = dayjs(p.value[0]).locale('fr').format('ddd D MMM YYYY');
        return `${day} · ${label} : ${formatMetricValue(p.value[1], format)}`;
      },
    },
    visualMap: {
      min: 0,
      max: max || 1,
      orient: 'horizontal' as const,
      left: 'center',
      bottom: 0,
      itemWidth: 10,
      itemHeight: 60,
      inRange: { color: buildColorRamp(base) },
      textStyle: { fontSize: 10, color: '#8B8FA7' },
      formatter: (value: number) => formatMetricValue(value, format, format === 'currency' ? { decimals: 0 } : undefined),
    },
    calendar: {
      top: hasTitle ? 50 : 30,
      left: 30,
      right: 30,
      bottom: 50,
      range,
      itemStyle: { borderWidth: 0.5, borderColor: '#FFFFFF', color: '#F4F5FA' },
      splitLine: { lineStyle: { color: '#E2E4ED' } },
      yearLabel: { show: false },
      dayLabel: { fontSize: 9, color: '#8B8FA7', nameMap: 'fr' },
      monthLabel: { fontSize: 10, color: '#8B8FA7', nameMap: 'fr' },
      cellSize: ['auto', 13] as ['auto', number],
    },
    series: [
      {
        type: 'heatmap',
        coordinateSystem: 'calendar',
        data: seriesData,
      },
    ],
  };
}

export default function CalendarHeatmapWidget({ config, data, loading, error }: WidgetProps) {
  // Hook avant tout early return (règles des hooks).
  const catalog = useFieldCatalog(data?.meta?.source_type as string | undefined);

  if (loading) {
    return (
      <div className={SHELL}>
        <div className="h-full w-full animate-pulse rounded bg-[#F4F5FA]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-red-600">{error}</span>
      </div>
    );
  }

  if (!data || !config.data_source_id || !(config.metrics ?? []).length) {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  const option = buildCalendarHeatmapOption(config, data, catalog);

  return (
    <div className={SHELL}>
      <ReactECharts option={option} notMerge style={{ height: '100%', minHeight: 180 }} />
    </div>
  );
}
